/* Ayudate · Ventanas emergentes */

import React, { useEffect } from "react";
import { createPortal } from "react-dom";

let _bloqueos = 0, _scrollGuardado = 0, _estiloPrevio = "";

function bloquearScroll() {
  if (_bloqueos++ > 0) return;
  _scrollGuardado = window.scrollY;
  _estiloPrevio = document.body.style.cssText;
  const ancho = window.innerWidth - document.documentElement.clientWidth;
  document.body.style.cssText += ";position:fixed;left:0;right:0;width:100%;overflow:hidden;"
    + `top:-${_scrollGuardado}px;padding-right:${ancho}px;`;
}

function liberarScroll() {
  if (--_bloqueos > 0) return;
  _bloqueos = 0;
  document.body.style.cssText = _estiloPrevio;
  window.scrollTo(0, _scrollGuardado);
}

/* Pregunta antes de descartar lo que la persona ya escribió.
   Perder un formulario a medio llenar sin aviso es de las cosas que más
   frustran, sobre todo a quien tardó en llenarlo.                        */

export function Capa({ children, onCerrar, alta = false, etiqueta, confirmarSalida = false,
                mensajeSalida = "Vas a perder lo que llevás escrito. ¿Cerrar de todas formas?" }) {
  const caja = React.useRef(null);
  const previo = React.useRef(null);

  /* Los datos de salida viven en un ref, no en las deps del useCallback: si
     confirmarSalida cambia mientras la persona escribe (por ejemplo, al
     llenar el primer campo), no debe recrearse "cerrar" y disparar de nuevo
     el efecto de abajo, que le robaría el foco al campo donde está escribiendo. */
  const salidaRef = React.useRef({ confirmarSalida, mensajeSalida, onCerrar });
  salidaRef.current = { confirmarSalida, mensajeSalida, onCerrar };

  const cerrar = React.useCallback(() => {
    const { confirmarSalida, mensajeSalida, onCerrar } = salidaRef.current;
    if (confirmarSalida && !window.confirm(mensajeSalida)) return;
    onCerrar?.();
  }, []);

  useEffect(() => {
    previo.current = document.activeElement;
    bloquearScroll();

    const enfocables = () => [...(caja.current?.querySelectorAll(
      'a[href],button:not([disabled]),input:not([disabled]),select,textarea,[tabindex]:not([tabindex="-1"])'
    ) ?? [])];

    const teclas = (e) => {
      if (e.key === "Escape") { e.preventDefault(); cerrar(); return; }
      if (e.key !== "Tab") return;
      const lista = enfocables();
      if (!lista.length) return;
      const primero = lista[0], ultimo = lista[lista.length - 1];
      if (e.shiftKey && document.activeElement === primero) { e.preventDefault(); ultimo.focus(); }
      else if (!e.shiftKey && document.activeElement === ultimo) { e.preventDefault(); primero.focus(); }
    };

    window.addEventListener("keydown", teclas, true);
    setTimeout(() => (enfocables()[0] ?? caja.current)?.focus(), 30);

    return () => {
      window.removeEventListener("keydown", teclas, true);
      liberarScroll();
      previo.current?.focus?.();
    };
  }, [cerrar]);

  const contenido = (
    <div className={`ov ${alta ? "ov-alta" : ""}`}
      onMouseDown={(e) => e.target === e.currentTarget && cerrar()}>
      <div className="ov-caja"
        onMouseDown={(e) => e.target === e.currentTarget && cerrar()}>
        <div ref={caja} role="dialog" aria-modal="true" aria-label={etiqueta} tabIndex={-1}>
          {children}
        </div>
      </div>
    </div>
  );

  /* Portal a la raíz ".v2" (nunca a document.body a secas, para conservar
     dentro del modal el filtro de daltonización, el zoom y las demás clases
     de accesibilidad, que se aplican con selectores ".v2.xxx .ov"). Sin este
     portal, un Capa abierto DESDE OTRO Capa (p. ej. el aviso de políticas
     que se abre encima de la agenda) queda anidado dentro del ".ov" de
     afuera, cuyo backdrop-filter lo convierte en el "containing block" de
     este overlay position:fixed: dejaría de fijarse al viewport y se vería
     cortado o descuadrado en vez de cubrir toda la pantalla. Portarlo como
     hermano, siempre directo bajo ".v2", evita ese anidamiento pase lo que
     pase y dos Capas se apilan solamente por z-index (ver .ov-alta). */
  const raiz = typeof document !== "undefined"
    ? document.querySelector(".v2") || document.body
    : null;
  return raiz ? createPortal(contenido, raiz) : contenido;
}
